import React from "react";

import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Pill, PillVariants } from "src/base-components/Pill";
import {
  getSchemaTypeIcon,
  SchemaType,
} from "src/base-components/SchemaTypeSelector";
import { type Option, Select } from "src/base-components/Select";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { CasePropertiesValue } from "src/clients/case-management";
import {
  CaseCreationNodeMetaMappedValue,
  CaseCreationNodeMetaMappedValueValueTypeEnum,
} from "src/clients/flow-api";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";

const getOptionsForProperty = (property: CasePropertiesValue) => {
  if (property.type === "enum") {
    const options: Option[] = [];

    property.values.forEach((value) => {
      options.push({
        value: (
          <Pill size="sm" variant={value.color as PillVariants}>
            <Pill.Text>{value.value}</Pill.Text>
          </Pill>
        ),
        key: value.value,
      });
    });

    return options;
  }
  return [];
};

type EnumAssignmentCardProps = {
  nodeId: string;
  fieldName: string;
  property: CasePropertiesValue;
  value: CaseCreationNodeMetaMappedValue;
  onChange: (value: CaseCreationNodeMetaMappedValue) => void;
  immutable: boolean;
  error?: boolean;
};

export const EnumAssignmentCard: React.FC<EnumAssignmentCardProps> = ({
  fieldName,
  property,
  value,
  nodeId,
  onChange,
  immutable,
  error,
}) => {
  const isSearchHighlighted = useIsFieldHighlighted(`${nodeId}:${fieldName}`);

  return (
    <Card className="w-full pt-4 pb-4">
      <SimpleRadioGroup
        className="space-x-4"
        disabled={immutable}
        orientation="horizontal"
        value={
          value.value_type ===
          CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED
            ? "fixed"
            : "dynamic"
        }
        onValueChange={(selectedValue) =>
          onChange({
            ...value,
            value: "",
            value_type:
              selectedValue === "fixed"
                ? CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED
                : CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC,
          })
        }
      >
        <SimpleRadioGroup.Item
          label="Use a predefined case property"
          labelClassName="ml-1.5 font-inter-normal-13px"
          value="fixed"
        />
        <SimpleRadioGroup.Item
          label="Map to a specific data field"
          labelClassName="ml-1.5 font-inter-normal-13px"
          value="dynamic"
        />
      </SimpleRadioGroup>
      {value.value_type ===
        CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED && (
        <div className="mt-5">
          <Select
            disabled={immutable}
            errored={error}
            options={getOptionsForProperty(property)}
            placeholder={`Select ${fieldName}`}
            value={value.value}
            fullWidth
            onChange={(selectedValue) => {
              onChange({
                ...value,
                value: selectedValue,
                value_type: CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED,
              });
            }}
          />
        </div>
      )}
      {value.value_type ===
        CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC && (
        <div className="mt-5">
          <div className="flex w-full items-center gap-2">
            <div className="w-1/2">
              <AutocompleteCodeInput
                disabled={immutable}
                error={error}
                isSearchHighlighted={isSearchHighlighted}
                placeholder="data.field"
                value={value.value}
                onChange={(selectedValue) => {
                  onChange({
                    ...value,
                    value: selectedValue,
                    value_type:
                      CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC,
                  });
                }}
              />
            </div>
            <div className="flex-shrink-0">
              <Icon color="text-gray-500" icon={faArrowRight} size="xs" />
            </div>
            <div className="w-1/2">
              <Input
                placeholder={property.display_name ?? fieldName}
                prefixIcon={{
                  icon: getSchemaTypeIcon(property.type as SchemaType),
                }}
                disabled
                fullWidth
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
